import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { APP_CONTENT_QUERY_KEYS } from '@/lib/constants/appContent';
import { AppContentApi } from './AppContentApi';
import { AppContentUtils } from './AppContentUtils';
import type { ListlistCreateReq, ListlistUpdateReq } from '@lib2/model/listlist/ListlistReq';
import type { ChildListlistOrListId } from '@lib2/model/listlist/ListlistReq';

// ── Listlist CRUD ──

export function useCreateListlist() {
  const { selectedCId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (req: ListlistCreateReq) =>
      AppContentApi.createListlist(selectedCId!, req),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: APP_CONTENT_QUERY_KEYS.listlists(selectedCId!),
      });
    },
  });
}

export function useUpdateListlist(llId: number) {
  const { selectedCId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (req: ListlistUpdateReq) =>
      AppContentApi.updateListlist(selectedCId!, llId, req),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: APP_CONTENT_QUERY_KEYS.listlists(selectedCId!),
      });
      queryClient.invalidateQueries({
        queryKey: APP_CONTENT_QUERY_KEYS.detail(selectedCId!, llId),
      });
    },
  });
}

export function useDeleteListlist() {
  const { selectedCId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (llId: number) =>
      AppContentApi.deleteListlist(selectedCId!, llId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: APP_CONTENT_QUERY_KEYS.listlists(selectedCId!),
      });
      queryClient.invalidateQueries({
        queryKey: APP_CONTENT_QUERY_KEYS.positions(selectedCId!),
      });
    },
  });
}

// ── Position Mutations (Hierarchy) ──

export function useAddChild() {
  const { selectedCId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      parentLLId,
      childId,
    }: {
      parentLLId: number;
      childId: ChildListlistOrListId;
    }) =>
      AppContentApi.createPosition(selectedCId!, { parentLLId, childId }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: APP_CONTENT_QUERY_KEYS.positions(selectedCId!),
      });
    },
  });
}

/**
 * Places several children (series, videos, streams) in a category in the
 * given order (design ADR-0014): one createPosition per pick, sequential so
 * positions land in pick order, then ONE positions invalidation. The server
 * appends at the end; `placeAtTop` follows the adds with one update-positions
 * call (picks first, then the current order) so the picked order is kept at
 * the top. A failure mid-way keeps the picks already placed (the error names
 * the pick that failed); the resolved value is the refs that were placed.
 */
export function useAddChildren() {
  const { selectedCId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      parentLLId,
      childIds,
      placeAtTop = false,
      currentOrder = [],
    }: {
      parentLLId: number;
      childIds: ChildListlistOrListId[];
      placeAtTop?: boolean;
      currentOrder?: ChildListlistOrListId[];
    }): Promise<ChildListlistOrListId[]> => {
      const placed: ChildListlistOrListId[] = [];
      for (const childId of childIds) {
        await AppContentApi.createPosition(selectedCId!, { parentLLId, childId });
        placed.push(childId);
      }
      if (placeAtTop && placed.length > 0) {
        await AppContentApi.updatePositions(selectedCId!, {
          parentLLId,
          childIds: AppContentUtils.withPicksAtTop(currentOrder, placed),
        });
      }
      return placed;
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: APP_CONTENT_QUERY_KEYS.positions(selectedCId!),
      });
    },
  });
}

export function useRemoveChild() {
  const { selectedCId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      parentLLId,
      childId,
    }: {
      parentLLId: number;
      childId: ChildListlistOrListId;
    }) =>
      AppContentApi.deletePosition(selectedCId!, { parentLLId, childId }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: APP_CONTENT_QUERY_KEYS.positions(selectedCId!),
      });
    },
  });
}

export function useSaveChildOrder() {
  const { selectedCId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      parentLLId,
      childIds,
    }: {
      parentLLId: number;
      childIds: ChildListlistOrListId[];
    }) =>
      AppContentApi.updatePositions(selectedCId!, { parentLLId, childIds }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: APP_CONTENT_QUERY_KEYS.positions(selectedCId!),
      });
    },
  });
}
